/**
 * The four Form 4180 interview lists (03-17) rendered through the shared
 * `CollectionCard` + `useCollection` (Add/Edit modal + per-row delete). Each is
 * keyed on the 4180 submission id. Banks fetch the full (unmasked) record on edit
 * since the list view returns a masked account number.
 */

import { useState } from "react";
import {
  type Column,
  CollectionCard,
} from "@/features/worksheets/components/collection-card";
import { useCollection } from "@/features/worksheets/hooks/use-collection";
import {
  banksApi,
  eftpsApi,
  individualsApi,
  positionsApi,
  type BankItem,
  type EftpsItem,
  type IndividualItem,
  type PositionItem,
} from "../api";
import {
  BankDialog,
  EftpsDialog,
  IndividualDialog,
  PositionDialog,
} from "./line-item-dialogs";

const dash = (v: string) => v || "—";

export function IndividualsList({ submissionId }: { submissionId: string }) {
  const { rows, loading, reload } = useCollection<IndividualItem>(
    submissionId,
    individualsApi.list,
  );
  const [dialog, setDialog] = useState<{ open: boolean; record: IndividualItem | null }>({
    open: false,
    record: null,
  });
  const columns: Column<IndividualItem>[] = [
    { header: "Name", cell: (r) => r.name },
    { header: "Contact number", cell: (r) => dash(r.phone) },
  ];
  return (
    <>
      <CollectionCard
        title="Other Individuals"
        addLabel="Add Individual"
        columns={columns}
        rows={rows}
        loading={loading}
        onAdd={() => setDialog({ open: true, record: null })}
        onEdit={(row) => setDialog({ open: true, record: row })}
        onDelete={(row) => individualsApi.remove(submissionId, row.id)}
      />
      <IndividualDialog
        submissionId={submissionId}
        record={dialog.record}
        open={dialog.open}
        onOpenChange={(open) => setDialog((d) => ({ ...d, open }))}
        onSaved={reload}
      />
    </>
  );
}

export function BanksList({ submissionId }: { submissionId: string }) {
  const { rows, loading, reload } = useCollection<BankItem>(submissionId, banksApi.list);
  const [dialog, setDialog] = useState<{ open: boolean; record: BankItem | null }>({
    open: false,
    record: null,
  });
  const columns: Column<BankItem>[] = [
    { header: "Bank name", cell: (r) => r.bankName },
    { header: "Account number", cell: (r) => dash(r.accountNumber) },
  ];
  // Fetch the full (unmasked) record before editing — the list masks it.
  async function openEdit(row: BankItem) {
    try {
      const full = await banksApi.get(submissionId, row.id);
      setDialog({ open: true, record: full });
    } catch {
      setDialog({ open: true, record: row });
    }
  }
  return (
    <>
      <CollectionCard
        title="Banks (signature authority)"
        addLabel="Add Bank"
        columns={columns}
        rows={rows}
        loading={loading}
        onAdd={() => setDialog({ open: true, record: null })}
        onEdit={(row) => void openEdit(row)}
        onDelete={(row) => banksApi.remove(submissionId, row.id)}
      />
      <BankDialog
        submissionId={submissionId}
        record={dialog.record}
        open={dialog.open}
        onOpenChange={(open) => setDialog((d) => ({ ...d, open }))}
        onSaved={reload}
      />
    </>
  );
}

export function EftpsList({ submissionId }: { submissionId: string }) {
  const { rows, loading, reload } = useCollection<EftpsItem>(submissionId, eftpsApi.list);
  const [dialog, setDialog] = useState<{ open: boolean; record: EftpsItem | null }>({
    open: false,
    record: null,
  });
  const columns: Column<EftpsItem>[] = [
    { header: "Where", cell: (r) => r.whereEftps },
    { header: "To whom assigned", cell: (r) => dash(r.whomEftps) },
  ];
  return (
    <>
      <CollectionCard
        title="EFTPS Locations"
        addLabel="Add Location"
        columns={columns}
        rows={rows}
        loading={loading}
        onAdd={() => setDialog({ open: true, record: null })}
        onEdit={(row) => setDialog({ open: true, record: row })}
        onDelete={(row) => eftpsApi.remove(submissionId, row.id)}
      />
      <EftpsDialog
        submissionId={submissionId}
        record={dialog.record}
        open={dialog.open}
        onOpenChange={(open) => setDialog((d) => ({ ...d, open }))}
        onSaved={reload}
      />
    </>
  );
}

export function PositionsList({ submissionId }: { submissionId: string }) {
  const { rows, loading, reload } = useCollection<PositionItem>(
    submissionId,
    positionsApi.list,
  );
  const [dialog, setDialog] = useState<{ open: boolean; record: PositionItem | null }>({
    open: false,
    record: null,
  });
  const columns: Column<PositionItem>[] = [
    { header: "Position", cell: (r) => r.position },
    { header: "Name", cell: (r) => dash(r.name) },
    { header: "Address", cell: (r) => dash(r.address) },
  ];
  return (
    <>
      <CollectionCard
        title="Corporate Positions"
        addLabel="Add Position"
        columns={columns}
        rows={rows}
        loading={loading}
        onAdd={() => setDialog({ open: true, record: null })}
        onEdit={(row) => setDialog({ open: true, record: row })}
        onDelete={(row) => positionsApi.remove(submissionId, row.id)}
      />
      <PositionDialog
        submissionId={submissionId}
        record={dialog.record}
        open={dialog.open}
        onOpenChange={(open) => setDialog((d) => ({ ...d, open }))}
        onSaved={reload}
      />
    </>
  );
}
